import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  FileText, 
  ClipboardCheck,
  ClipboardList,
  Calendar,
  CalendarDays,
  Download,
  Target,
  Layers,
  CheckCircle2,
  Printer,
  GraduationCap,
  Languages,
  MessageCircle,
  BookMarked,
  PenTool,
  Headphones,
  List,
  Sparkles,
  Clock,
  Globe,
  FileQuestion,
  Files
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { toast } from 'sonner';

import { frenchSeniorCurriculum, frenchSeniorWeeklyLessons } from '@/data/frenchSeniorCurriculum';
import { seniorFrenchWeeklyLessons } from '@/data/seniorFrenchWeeklyLessons';
import { 
  year11FrenchUnitTests,
  year12FrenchUnitTests,
  year13FrenchUnitTests
} from '@/data/seniorFrenchUnitTests';
import { 
  year11FrenchStudentNotes,
  year12FrenchStudentNotes,
  year13FrenchStudentNotes
} from '@/data/seniorFrenchStudentNotes';
import AILessonPlanGenerator from '@/components/curriculum/AILessonPlanGenerator';
import SubjectSchemeOfWork from '@/components/curriculum/SubjectSchemeOfWork';
import LessonPlanGenerator from '@/components/curriculum/LessonPlanGenerator';
import TermExamGenerator from '@/components/curriculum/TermExamGenerator';
import QuickPrintAll from '@/components/curriculum/QuickPrintAll';
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';

export default function SeniorFrenchCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 11");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [selectedTerm, setSelectedTerm] = useState("term1");
  const [teacherYears, setTeacherYears] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  useEffect(() => {
    const fetchTeacherClasses = async () => {
      try {
        const classes = await apiService.getTeacherClasses(true);
        const years = [...new Set(classes
          .filter(cls => 
            cls.subject?.toLowerCase().includes('french')
          )
          .map(cls => cls.yearLevel)
        )].sort();
        
        if (years.length > 0) {
          setTeacherYears(years);
          const yearNum = parseInt(years[0].replace(/\D/g, ''));
          if (yearNum >= 11 && yearNum <= 13) {
            setSelectedYear(`Year ${yearNum}`);
          }
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherClasses();
  }, []);

  const curriculum = frenchSeniorCurriculum;
  const yearKey = selectedYear.toLowerCase().replace(' ', '');
  const currentYearData = curriculum[yearKey];
  
  const getStudentNotes = () => {
    switch(selectedYear) {
      case "Year 11": return year11FrenchStudentNotes;
      case "Year 12": return year12FrenchStudentNotes;
      case "Year 13": return year13FrenchStudentNotes;
      default: return year11FrenchStudentNotes;
    }
  };

  const getUnitTests = () => {
    switch(selectedYear) {
      case "Year 11": return year11FrenchUnitTests;
      case "Year 12": return year12FrenchUnitTests;
      case "Year 13": return year13FrenchUnitTests;
      default: return year11FrenchUnitTests;
    }
  };

  const strands = currentYearData?.strands || [];
  const studentNotes = getStudentNotes();
  const unitTests = getUnitTests();

  // Use the new weekly lessons from frenchSeniorCurriculum.js, fallback to old
  const weeklyLessonsData = frenchSeniorWeeklyLessons || seniorFrenchWeeklyLessons;

  const strandColorMap = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', icon: 'text-blue-600', badge: 'bg-blue-500' },
    green: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-300', icon: 'text-green-600', badge: 'bg-green-500' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300', icon: 'text-purple-600', badge: 'bg-purple-500' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', icon: 'text-orange-600', badge: 'bg-orange-500' },
  };

  const getStrandColors = (color) => strandColorMap[color] || strandColorMap.blue;

  const getStrandIcon = (code) => {
    switch(code) {
      case 'FLS1': return <BookMarked className="h-5 w-5 text-blue-600" />;
      case 'FLS2': return <BookOpen className="h-5 w-5 text-green-600" />;
      case 'FLS3': return <PenTool className="h-5 w-5 text-purple-600" />;
      case 'FLS4': return <Headphones className="h-5 w-5 text-orange-600" />;
      default: return <Globe className="h-5 w-5 text-teal-600" />;
    }
  };

  const handlePrint = () => {
    window.print();
    toast.success('Curriculum sent to printer');
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <Card className="bg-gradient-to-r from-indigo-600 to-blue-700 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Languages className="h-8 w-8" />
              <div>
                <CardTitle className="text-2xl">Français Langue Seconde (FLS)</CardTitle>
                <CardDescription className="text-indigo-100">
                  {currentYearData?.title || `Senior French • Years 11-13`} • Programme Révisé 2024
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[120px] bg-white/20 border-white/30 text-white">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Year 11">Year 11</SelectItem>
                  <SelectItem value="Year 12">Year 12</SelectItem>
                  <SelectItem value="Year 13">Year 13</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="secondary" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-100 p-1 flex-wrap">
          <TabsTrigger value="curriculum" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <BookOpen className="h-4 w-4 mr-2" />
            Programme
          </TabsTrigger>
          <TabsTrigger value="schemes" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <CalendarDays className="h-4 w-4 mr-2" />
            Schéma
          </TabsTrigger>
          <TabsTrigger value="term-exam" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white bg-rose-100">
            <FileQuestion className="h-4 w-4 mr-2" />
            Examen
          </TabsTrigger>
          <TabsTrigger value="notes" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <FileText className="h-4 w-4 mr-2" />
            Notes d'étude
          </TabsTrigger>
          <TabsTrigger value="tests" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <ClipboardCheck className="h-4 w-4 mr-2" />
            Évaluations
          </TabsTrigger>
          <TabsTrigger value="lesson-plans" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <PenTool className="h-4 w-4 mr-2" />
            Plans de Cours
          </TabsTrigger>
          <TabsTrigger value="quick-print" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white bg-blue-100">
            <Files className="h-4 w-4 mr-2" />
            Impression
          </TabsTrigger>
          <TabsTrigger value="ai-planner" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <Sparkles className="h-4 w-4 mr-2" />
            AI Lesson Plans
          </TabsTrigger>
        </TabsList>

        {/* Schemes Tab - Weekly Scheme of Work */}
        <TabsContent value="schemes" className="space-y-6">
          <SubjectSchemeOfWork
            subject="Senior French"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={weeklyLessonsData}
            subjectColor="indigo"
          />
        </TabsContent>

        {/* Term Exam Tab */}
        <TabsContent value="term-exam" className="space-y-6">
          <TermExamGenerator
            subject="French"
            year={parseInt(selectedYear.replace('Year ', ''))}
            unitTests={selectedYear === 'Year 11' ? year11FrenchUnitTests : selectedYear === 'Year 12' ? year12FrenchUnitTests : year13FrenchUnitTests}
            subjectColor="indigo"
          />
        </TabsContent>

        {/* Quick Print Tab */}
        <TabsContent value="quick-print" className="space-y-6">
          <QuickPrintAll
            subject="Senior French"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={weeklyLessonsData}
            subjectColor="indigo"
          />
        </TabsContent>

        {/* Lesson Plans Tab */}
        <TabsContent value="lesson-plans" className="space-y-6">
          <LessonPlanGenerator
            subject="Senior French"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={weeklyLessonsData}
            subjectColor="indigo"
          />
        </TabsContent>

        {/* Curriculum Tab */}
        <TabsContent value="curriculum" className="space-y-6">
          {/* Year Description */}
          {currentYearData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-indigo-600" />
                  {currentYearData.title}
                </CardTitle>
                <CardDescription>{currentYearData.description}</CardDescription>
              </CardHeader>
            </Card>
          )}

          {/* Strands with SubStrands and SLOs */}
          {strands.map((strand) => {
            const colors = getStrandColors(strand.color);
            const totalSLOs = strand.subStrands?.reduce((acc, ss) => acc + (ss.slos?.length || 0), 0) || 0;
            return (
              <Card key={strand.id} data-testid={`strand-${strand.code}`}>
                <CardHeader className={`${colors.bg} border-b`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getStrandIcon(strand.code)}
                      <div>
                        <CardTitle className={`text-lg ${colors.text}`}>
                          {strand.code} - {strand.name}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {strand.subStrands?.length || 0} sous-thèmes | {totalSLOs} objectifs d'apprentissage
                        </CardDescription>
                      </div>
                    </div>
                    <Badge className={`${colors.badge} text-white`}>{strand.code}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <Accordion type="multiple" className="space-y-2">
                    {strand.subStrands?.map((subStrand) => (
                      <AccordionItem key={subStrand.id} value={subStrand.id} className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-2 text-left">
                            <Badge variant="outline" className={`${colors.text} ${colors.border} shrink-0`}>
                              {subStrand.slos?.length || 0} SLOs
                            </Badge>
                            <span className="font-semibold text-sm">{subStrand.name}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-3">
                          <div className="space-y-2">
                            {subStrand.slos?.map((slo, sIdx) => (
                              <div key={sIdx} className="flex items-start gap-3 p-2 bg-gray-50 rounded-lg">
                                <Badge variant="secondary" className="text-xs font-mono shrink-0 mt-0.5">
                                  {slo.code}
                                </Badge>
                                <span className="text-sm text-gray-700">{slo.description}</span>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            );
          })}

          {/* Assessment Structure */}
          {currentYearData?.assessment && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-indigo-600" />
                  Structure d'évaluation - {selectedYear}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="border-2 border-indigo-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Target className="h-4 w-4 text-indigo-600" />
                        {currentYearData.assessment.internal.type} ({currentYearData.assessment.internal.weighting})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {currentYearData.assessment.internal.tasks && (
                        <ul className="space-y-1 text-sm text-gray-600">
                          {currentYearData.assessment.internal.tasks.map((task, i) => (
                            <li key={i} className="flex items-center gap-2">
                              <CheckCircle2 className="h-3 w-3 text-indigo-500" />
                              <span className="font-medium">{task.name}:</span> {task.description}
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="mt-2">
                        <Progress value={parseInt(currentYearData.assessment.internal.weighting)} className="h-2" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-2 border-blue-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <PenTool className="h-4 w-4 text-blue-600" />
                        {currentYearData.assessment.external.type} ({currentYearData.assessment.external.weighting})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {currentYearData.assessment.external.duration && (
                        <p className="text-sm text-gray-600">Durée: {currentYearData.assessment.external.duration}</p>
                      )}
                      <div className="mt-2">
                        <Progress value={parseInt(currentYearData.assessment.external.weighting)} className="h-2" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Study Notes Tab */}
        <TabsContent value="notes" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-indigo-600" />
                Notes d'étude - {selectedYear}
              </CardTitle>
              <CardDescription>Notes complètes pour tous les thèmes</CardDescription>
            </CardHeader>
            <CardContent>
              {studentNotes?.strands && Object.entries(studentNotes.strands).map(([strandKey, strand]) => (
                <div key={strandKey} className="mb-6">
                  <h3 className="text-lg font-bold text-indigo-700 mb-4">{strand.name}</h3>
                  <Accordion type="single" collapsible className="space-y-2">
                    {strand.units?.map((unit, idx) => (
                      <AccordionItem key={idx} value={`notes-${strandKey}-${idx}`} className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <span className="font-semibold">{unit.name}</span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-4 space-y-4">
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">Objectifs:</h4>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                              {unit.objectives?.map((obj, i) => (
                                <li key={i}>{obj}</li>
                              ))}
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">Vocabulaire clé:</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {unit.keyTerms?.map((term, i) => (
                                <div key={i} className="bg-indigo-50 p-2 rounded">
                                  <span className="font-medium text-indigo-700">{term.term}:</span>
                                  <span className="text-sm text-gray-600 ml-1">{term.definition}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">Notes d'étude:</h4>
                            <div className="prose prose-sm max-w-none bg-gray-50 p-4 rounded-lg">
                              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                                {unit.studyNotes}
                              </pre>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">Questions pratiques:</h4>
                            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                              {unit.practiceQuestions?.map((q, i) => (
                                <li key={i}>{q}</li>
                              ))}
                            </ol>
                          </div>
                          
                          {unit.examTips && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                              <h4 className="font-semibold text-sm text-yellow-800 mb-1">💡 Conseils d'examen:</h4>
                              <p className="text-sm text-yellow-700">{unit.examTips}</p>
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unit Tests Tab */}
        <TabsContent value="tests" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-indigo-600" />
                Évaluations - {selectedYear}
              </CardTitle>
              <CardDescription>Questions d'évaluation et réponses</CardDescription>
            </CardHeader>
            <CardContent>
              {unitTests?.strands && Object.entries(unitTests.strands).map(([strandKey, strand]) => (
                <div key={strandKey} className="mb-6">
                  <h3 className="text-lg font-bold text-indigo-700 mb-4">{strand.name}</h3>
                  <Accordion type="single" collapsible className="space-y-2">
                    {strand.tests?.map((test, idx) => (
                      <AccordionItem key={idx} value={`test-${strandKey}-${idx}`} className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{test.title}</span>
                            <Badge variant="outline">{test.duration}</Badge>
                            <Badge variant="secondary">{test.totalMarks} points</Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-4">
                          <div className="space-y-4">
                            {test.questions?.map((q, qIdx) => (
                              <Card key={qIdx} className="border">
                                <CardContent className="pt-4">
                                  <div className="flex justify-between items-start mb-2">
                                    <span className="font-semibold text-indigo-700">Question {q.id}</span>
                                    <Badge>{q.marks} points</Badge>
                                  </div>
                                  <div className="mb-3 whitespace-pre-line text-gray-700">{q.question}</div>
                                  <Accordion type="single" collapsible>
                                    <AccordionItem value="answer" className="border-0">
                                      <AccordionTrigger className="text-sm text-indigo-600 py-2">
                                        Afficher la réponse
                                      </AccordionTrigger>
                                      <AccordionContent>
                                        <div className="bg-green-50 p-3 rounded whitespace-pre-line text-sm text-gray-700">
                                          {q.answer}
                                        </div>
                                      </AccordionContent>
                                    </AccordionItem>
                                  </Accordion>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Lesson Plans Tab */}
        <TabsContent value="ai-planner">
          <AILessonPlanGenerator 
            subject="French (FLS)"
            year={selectedYear}
            curriculumData={currentYearData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
